import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedStudent } from '@/State/usersSlice';
import { db, functions } from '@/firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const DataPrefetcher = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.users.currentUser);

  useEffect(() => {
    const initializeGuardianStudent = async () => {
      // Only run for guardians
      if (!user || user.selectedAffiliation?.role !== 'guardian') {
        return;
      }

      try {
        console.log('DataPrefetcher: Initializing guardian student selection');
        
        // 1. Fetch the full list of students first. 
        // This is the source of truth for names and details.
        const getGuardianStudents = httpsCallable(functions, 'getGuardianStudents');
        const result = await getGuardianStudents();
        const data = result.data as { students: any[] };
        
        if (!data.students || data.students.length === 0) {
          console.log('DataPrefetcher: No students found for guardian');
          return;
        }

        // 2. Determine which student to select
        let studentToSelect = null;

        // Check localStorage first for the UID
        const localCached = localStorage.getItem(`lastStudent_${user.id}`);
        if (localCached) {
          try {
            const cached = JSON.parse(localCached);
            studentToSelect = data.students.find(s => s.studentUid === cached.studentUid);
            if (studentToSelect) console.log('DataPrefetcher: Found matching student from localStorage');
          } catch (e) {
            console.error('DataPrefetcher: Error parsing localStorage student', e);
          }
        }

        // If not in localStorage, check Firestore
        if (!studentToSelect) {
          const userPrefsRef = doc(db, "users", user.id, "preferences", "general");
          const prefsSnap = await getDoc(userPrefsRef);
          if (prefsSnap.exists() && prefsSnap.data().lastSelectedStudent) {
            const lastSelected = prefsSnap.data().lastSelectedStudent;
            studentToSelect = data.students.find(s => s.studentUid === lastSelected.studentUid);
            if (studentToSelect) console.log('DataPrefetcher: Found matching student from Firestore');
          }
        }

        // If still no student, pick the first one
        if (!studentToSelect) {
          studentToSelect = data.students[0];
          console.log('DataPrefetcher: Defaulting to first student');
        }

        if (studentToSelect) {
          const payload = {
            studentUid: studentToSelect.studentUid,
            firstName: studentToSelect.firstName,
            lastName: studentToSelect.lastName,
            registrationNumber: studentToSelect.registrationNumber,
            schoolID: studentToSelect.schoolID
          };

          // Only dispatch if it's different from current or if current is missing names
          const current = user.selectedStudent;
          const isMissingNames = current && (!current.firstName || !current.lastName);
          
          if (!current || current.studentUid !== payload.studentUid || isMissingNames) {
            console.log('DataPrefetcher: Setting selected student:', payload.firstName, payload.lastName);
            dispatch(setSelectedStudent(payload));
            
            // Update persistence
            localStorage.setItem(`lastStudent_${user.id}`, JSON.stringify(payload));
            const userPrefsRef = doc(db, "users", user.id, "preferences", "general");
            await setDoc(userPrefsRef, { lastSelectedStudent: payload }, { merge: true });
          }
        }
      } catch (error) {
        console.error('DataPrefetcher: Error initializing guardian student:', error);
      }
    };

    initializeGuardianStudent();
  }, [user?.id, user?.selectedAffiliation?.role, dispatch]);

  return null;
};

export default DataPrefetcher;
